import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Copy, Loader2, Mail, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, brl, formatApiError } from "@/lib/api";
import { LogoMark } from "./Logo";

const STEPS = ["Serviço", "Dados", "Pronto"];

export default function OrderDialog({ open, onOpenChange, services, initialService }) {
  const [step, setStep] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_whatsapp: "", brief: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [pixKey, setPixKey] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => setPixKey(r.data.pix_key || "")).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      setStep(initialService ? 1 : 0);
      setSelectedId(initialService?.id || null);
      setResult(null);
      setCopied(false);
    }
  }, [open, initialService]);

  const selected = useMemo(() => services.find((s) => s.id === selectedId) || null, [services, selectedId]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    if (!form.client_name.trim() || !form.client_email.trim() || !form.client_whatsapp.trim() || form.brief.trim().length < 10) {
      toast.error("Preencha nome, e-mail, WhatsApp e um briefing com pelo menos 10 caracteres.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/orders", { ...form, service_id: selectedId });
      setResult(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível registrar o pedido."));
    } finally {
      setSending(false);
    }
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(pixKey);
      setCopied(true);
      toast.success("Chave Pix copiada!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-ink-3 focus:border-accent";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl border-line bg-paper sm:max-w-lg" data-testid="order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-display text-2xl font-extrabold tracking-tight">
            <LogoMark className="h-7 w-7" />
            {step === 0 && "Escolha o serviço"}
            {step === 1 && "Seus dados"}
            {step === 2 && "Pedido registrado"}
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div data-testid="order-step-service">
            <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto pr-1">
              {services.map((s) => (
                <button
                  key={s.id}
                  data-testid={`order-service-option-${s.id}`}
                  onClick={() => setSelectedId(s.id)}
                  className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-colors ${
                    selectedId === s.id ? "border-accent bg-accent-subtle" : "border-line bg-white hover:border-ink"
                  }`}
                >
                  <span>
                    <span className="block font-display text-base font-bold">{s.title}</span>
                    <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-3">
                      {s.delivery_days} dias úteis
                    </span>
                  </span>
                  <span className="font-display text-lg font-extrabold">{brl(s.price)}</span>
                </button>
              ))}
            </div>
            <button
              data-testid="order-step-continue-button"
              disabled={!selectedId}
              onClick={() => setStep(1)}
              className="btn-lift mt-5 w-full rounded-full bg-ink py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper disabled:cursor-not-allowed disabled:opacity-40 hover:bg-accent"
            >
              Continuar
            </button>
          </div>
        )}

        {step === 1 && selected && (
          <div className="flex flex-col gap-3" data-testid="order-step-details">
            <div className="flex items-center justify-between rounded-xl bg-paper2 px-5 py-3">
              <span className="font-display text-sm font-bold">{selected.title}</span>
              <span className="font-display font-extrabold">{brl(selected.price)}</span>
            </div>
            <input data-testid="order-form-name-input" placeholder="Seu nome completo" value={form.client_name} onChange={setField("client_name")} className={inputCls} />
            <input data-testid="order-form-email-input" type="email" placeholder="Seu e-mail (enviaremos a confirmação)" value={form.client_email} onChange={setField("client_email")} className={inputCls} />
            <input data-testid="order-form-whatsapp-input" placeholder="Seu WhatsApp com DDD" value={form.client_whatsapp} onChange={setField("client_whatsapp")} className={inputCls} />
            <textarea
              data-testid="order-form-brief-input"
              placeholder="Conte sobre o projeto: nome da marca, referências, o que você precisa..."
              value={form.brief}
              onChange={setField("brief")}
              rows={4}
              className={`${inputCls} resize-none`}
            />
            <div className="mt-1 flex gap-3">
              <button
                data-testid="order-form-back-button"
                onClick={() => setStep(0)}
                className="btn-lift rounded-full border border-ink px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                Voltar
              </button>
              <button
                data-testid="order-form-submit-button"
                onClick={submit}
                disabled={sending}
                className="btn-lift flex flex-1 items-center justify-center gap-2 rounded-full bg-accent py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white hover:bg-accent-hover disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : `Enviar pedido · ${brl(selected.price)}`}
              </button>
            </div>
          </div>
        )}

        {step === 2 && result && (
          <div className="flex flex-col gap-4" data-testid="order-step-success">
            <div className="flex items-center gap-4 rounded-xl bg-pix-subtle p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pix text-white">
                <PartyPopper className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-lg font-bold">Pedido {result.order.code} registrado!</p>
                <p className="text-sm text-ink-2">
                  {result.order.service_title} · {brl(result.order.service_price)}
                </p>
              </div>
            </div>

            {pixKey ? (
              <div className="rounded-xl border border-line bg-white p-5" data-testid="pix-instructions">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3">Pagamento via Pix — copia e cola</p>
                <div className="mt-3 flex items-center gap-2">
                  <code data-testid="pix-key-value" className="flex-1 truncate rounded-lg bg-paper2 px-4 py-3 font-mono text-sm">
                    {pixKey}
                  </code>
                  <button
                    data-testid="pix-copy-key-button"
                    onClick={copyPix}
                    className="btn-lift flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-ink text-paper hover:bg-accent"
                    aria-label="Copiar chave Pix"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-sm text-ink-2">
                  <li>Faça o Pix de {brl(result.order.service_price)} usando a chave acima.</li>
                  <li>Envie o comprovante ao nosso WhatsApp (número enviado por e-mail).</li>
                  <li>Confirmado o pagamento, começamos o seu briefing.</li>
                </ol>
              </div>
            ) : (
              <p className="rounded-xl border border-line bg-white p-5 text-sm text-ink-2">
                A chave Pix e as instruções de pagamento serão enviadas para o seu e-mail.
              </p>
            )}

            <div className="flex items-start gap-3 rounded-xl border border-line bg-white p-5 text-sm text-ink-2" data-testid="order-email-note">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p>
                Enviamos um e-mail para <strong className="text-ink">{result.order.client_email}</strong> com o resumo do pedido, o
                valor e nosso número de contato do estúdio.
              </p>
            </div>

            <button
              data-testid="order-success-close-button"
              onClick={() => onOpenChange(false)}
              className="btn-lift rounded-full bg-ink py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper hover:bg-accent"
            >
              Fechar
            </button>
          </div>
        )}

        {step < 2 && (
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3">
            Passo {step + 1} de 2 — {STEPS[step]}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
